import React, { useEffect, useState } from 'react';
import { Target, Lightbulb, ShieldCheck, Users, ChevronRight, Rocket, ArrowUpRight, Wifi, Sparkles } from 'lucide-react';
import newBg from '../assets/About.png';
import vdtLogoVision from '../assets/Brand.jpeg';
import ourVisionImg from '../assets/vision_futuristic.png';
import missionImg from '../assets/mission_futuristic.png';
import SEO from './SEO';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Add a slight delay for the entrance animation
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  }, []);

  const stats = [
    { label: 'Years Experience', value: '3+' },
    { label: 'Projects Delivered', value: '50+' },
    { label: 'Happy Clients', value: '30+' },
    { label: 'Team Members', value: '15+' }
  ];

  const values = [
    { icon: Lightbulb, title: 'Innovation', desc: 'We constantly push the boundaries of what is possible with emerging technologies.' },
    { icon: Target, title: 'Excellence', desc: 'We deliver top-tier solutions that exceed expectations and drive real business growth.' },
    { icon: Users, title: 'Client-Centric', desc: 'Your success is our success. We build lasting partnerships based on trust and results.' },
    { icon: ShieldCheck, title: 'Integrity', desc: 'We operate with complete transparency, honesty, and uncompromising ethical standards.' }
  ];

  return (
    <div className="bg-[#050810] min-h-screen text-white pt-24 pb-20 relative overflow-hidden" id="about" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="About Us – Vishwa DigiTech"
        description="Learn about Vishwa DigiTech, Sangli's leading IT and digital marketing company. Meet our expert team, discover our vision, mission and values. 50+ projects delivered, 30+ happy clients."
        keywords="about vishwa digitech, IT company sangli, digital agency sangli maharashtra, web development team sangli, who is vishwa digitech"
        canonicalPath="/about"
      />
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute top-[10%] left-[5%] w-96 h-96 bg-[#0EA5E9]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[20%] right-[5%] w-[500px] h-[500px] bg-[#1A4A8B]/20 rounded-full blur-[150px]" />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='rgba(14,165,233,0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")` }}
        />
      </div>

      {/* Hero Section with New Background (FULL WIDTH) */}
      <div className={`relative w-full overflow-hidden mb-24 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} border-b border-white/10`}>
        {/* Make sure the image name matches what you save in assets folder! */}
        <img src={newBg} alt="VishwaDigitech Team" className="absolute inset-0 w-full h-full object-cover object-center" />
        {/* <div className="absolute inset-0 bg-gradient-to-r from-[#050810] via-[#050810]/95 to-[#050810]/40 md:to-transparent"></div> */}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="py-24 md:py-36 lg:py-48 max-w-3xl">
            <div className={`transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="inline-block px-5 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-6 backdrop-blur-md shadow-lg">
                Who We Are
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-[#0EA5E9] mb-6 tracking-tight">
                ABOUT US
              </h1>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed font-light">
                Vishwa DigiTech delivers innovative IT and digital solutions that help <br></br> businesses build a strong online presence and achieve sustainable growth.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Logo & Brand Vision Section */}
        <div className="mb-32">
          <div className={`text-center mb-16 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Sparkles className="w-4 h-4" />
              Our Identity
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              The Meaning Behind <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Our Brand</span>
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed font-light">
              At Vishwa DigiTech, our logo represents more than just a brand—it reflects our vision and commitment to digital innovation and growth.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left side: Bento Grid Details */}
            <div className={`lg:col-span-7 grid sm:grid-cols-2 gap-6 transition-all duration-1000 delay-700 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'} order-2 lg:order-1`}>

              {/* Card 1: Upward Arrow */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#0EA5E9]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0EA5E9]/10 rounded-bl-full -z-10 group-hover:bg-[#0EA5E9]/20 transition-colors duration-500" />

                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/20 to-transparent border border-[#0EA5E9]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(14,165,233,0.15)]">
                    <ArrowUpRight className="w-8 h-8 text-[#0EA5E9]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#0EA5E9] transition-colors">Upward Arrow</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Symbolizes continuous <strong className="text-gray-200">growth, progress, and success</strong>. It represents our core mission to help businesses achieve sustainable upward trajectories through strategic digital solutions.
                  </p>
                </div>
              </div>

              {/* Card 2: Wi-Fi Signal */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#06B6D4]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#06B6D4]/10 rounded-bl-full -z-10 group-hover:bg-[#06B6D4]/20 transition-colors duration-500" />

                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06B6D4]/20 to-transparent border border-[#06B6D4]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(6,182,212,0.15)]">
                    <Wifi className="w-8 h-8 text-[#06B6D4]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#06B6D4] transition-colors">Wireless Signal</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Signifies global <strong className="text-gray-200">connectivity and technology</strong>. It highlights our deep-rooted expertise in modern IT solutions and the limitless power of the internet.
                  </p>
                </div>
              </div>

              {/* Card 3: Quote */}
              <div className="sm:col-span-2 bg-gradient-to-br from-[#0EA5E9]/10 to-transparent border border-[#0EA5E9]/20 p-8 md:p-10 rounded-3xl relative overflow-hidden group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#0EA5E9]/0 via-[#0EA5E9]/10 to-[#0EA5E9]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
                <p className="text-gray-300 font-medium leading-relaxed text-lg md:text-xl italic relative z-10">
                  <span className="text-4xl text-[#0EA5E9] absolute -top-4 -left-2 opacity-50">"</span>
                  <span className="pl-6 block">
                    Together, these elements embody our belief in leveraging technology and innovation to create meaningful digital experiences and empower businesses in a connected world.
                  </span>
                </p>
              </div>

            </div>

            {/* Right side: Logo Image Display */}
            <div className={`lg:col-span-5 relative transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'} order-1 lg:order-2`}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-[#0EA5E9]/20 to-[#1A4A8B]/30 rounded-full blur-3xl -z-10 animate-pulse" />

              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative bg-[#050810] border border-white/10 rounded-[2rem] overflow-hidden p-4">
                  <div className="relative rounded-[1.5rem] overflow-hidden">
                    <img src={vdtLogoVision} alt="Vishwa DigiTech Logo Meaning" className="w-full h-auto object-cover transform group-hover:scale-110 transition-transform duration-1000 ease-in-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1F]/80 via-[#0A0F1F]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>        {/* Vision Section */}
        <div className="mb-32">
          <div className={`text-center mb-16 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Target className="w-4 h-4" />
              Our Vision
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              Empowering Brands for <br className="hidden md:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Tomorrow</span>
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed font-light">
              We aspire to become a trusted technology partner, helping brands thrive in an ever-evolving digital world through creativity, innovation, and excellence.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left side: Custom Vision Image Display */}
            <div className={`lg:col-span-5 relative transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'} order-1`}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-[#0EA5E9]/20 to-[#1A4A8B]/30 rounded-full blur-3xl -z-10 animate-pulse" />
              
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative bg-[#050810] border border-white/10 rounded-[2rem] overflow-hidden p-4">
                  <div className="relative rounded-[1.5rem] overflow-hidden">
                    <img src={ourVisionImg} alt="Vishwa DigiTech Vision" className="w-full h-auto object-cover transform group-hover:scale-110 transition-transform duration-1000 ease-in-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1F]/80 via-[#0A0F1F]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Bento Grid Details */}
            <div className={`lg:col-span-7 grid sm:grid-cols-2 gap-6 transition-all duration-1000 delay-700 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'} order-2`}>
              
              {/* Card 1: IT & Marketing Innovation */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#0EA5E9]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0EA5E9]/10 rounded-bl-full -z-10 group-hover:bg-[#0EA5E9]/20 transition-colors duration-500" />
                
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/20 to-transparent border border-[#0EA5E9]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(14,165,233,0.15)]">
                    <Lightbulb className="w-8 h-8 text-[#0EA5E9]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#0EA5E9] transition-colors">IT & Marketing</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Empowering businesses with innovative <strong className="text-gray-200">digital marketing and cutting-edge IT solutions</strong> that drive sustainable growth and position your brand at the forefront of digital excellence.
                  </p>
                </div>
              </div>

              {/* Card 2: Connectivity & Success */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#06B6D4]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#06B6D4]/10 rounded-bl-full -z-10 group-hover:bg-[#06B6D4]/20 transition-colors duration-500" />
                
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06B6D4]/20 to-transparent border border-[#06B6D4]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(6,182,212,0.15)]">
                    <Wifi className="w-8 h-8 text-[#06B6D4]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#06B6D4] transition-colors">Connectivity</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Fostering deep <strong className="text-gray-200">network connectivity and lasting success</strong>. We build channels that establish real connectivity between brands and users, helping enterprises thrive globally.
                  </p>
                </div>
              </div>

              {/* Card 3: Main Vision Quote */}
              <div className="sm:col-span-2 bg-gradient-to-br from-[#0EA5E9]/10 to-transparent border border-[#0EA5E9]/20 p-8 md:p-10 rounded-3xl relative overflow-hidden group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#0EA5E9]/0 via-[#0EA5E9]/10 to-[#0EA5E9]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
                <p className="text-gray-300 font-medium leading-relaxed text-lg md:text-xl italic relative z-10">
                  <span className="text-4xl text-[#0EA5E9] absolute -top-4 -left-2 opacity-50">"</span>
                  <span className="pl-6 block">
                    At Vishwa DigiTech, our vision is to empower businesses with innovative digital marketing and cutting-edge IT solutions that drive growth, foster connectivity, and create lasting success.
                  </span>
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="mb-32">
          <div className={`text-center mb-16 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Rocket className="w-4 h-4" />
              Our Mission
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              Transforming Ideas into <br className="hidden md:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Impactful Experiences</span>
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed font-light">
              Through creativity, innovation, and a commitment to excellence, we aim to deliver solutions that transform ideas into impactful digital experiences.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left side: Bento Grid Details */}
            <div className={`lg:col-span-7 grid sm:grid-cols-2 gap-6 transition-all duration-1000 delay-700 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'} order-2 lg:order-1`}>
              
              {/* Card 1: Progress & Growth */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#0EA5E9]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0EA5E9]/10 rounded-bl-full -z-10 group-hover:bg-[#0EA5E9]/20 transition-colors duration-500" />
                
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/20 to-transparent border border-[#0EA5E9]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(14,165,233,0.15)]">
                    <ArrowUpRight className="w-8 h-8 text-[#0EA5E9]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#0EA5E9] transition-colors">Progress & Growth</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Helping brands leverage the power of technology and the internet to <strong className="text-gray-200">unlock new opportunities</strong>, enhance digital presence, and achieve long-term success.
                  </p>
                </div>
              </div>

              {/* Card 2: Innovation & Creativity */}
              <div className="bg-white/[0.02] border border-white/5 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#06B6D4]/30 transition-all duration-500 group relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#06B6D4]/10 rounded-bl-full -z-10 group-hover:bg-[#06B6D4]/20 transition-colors duration-500" />
                
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#06B6D4]/20 to-transparent border border-[#06B6D4]/30 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(6,182,212,0.15)]">
                    <Sparkles className="w-8 h-8 text-[#06B6D4]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#06B6D4] transition-colors">Creativity</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                    Crafting <strong className="text-gray-200">transformative digital experiences</strong>. Inspired by connectivity, we deliver creative and innovative solutions that drive true digital transformation.
                  </p>
                </div>
              </div>

              {/* Card 3: Main Mission Quote */}
              <div className="sm:col-span-2 bg-gradient-to-br from-[#0EA5E9]/10 to-transparent border border-[#0EA5E9]/20 p-8 md:p-10 rounded-3xl relative overflow-hidden group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#0EA5E9]/0 via-[#0EA5E9]/10 to-[#0EA5E9]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
                <p className="text-gray-300 font-medium leading-relaxed text-lg md:text-xl italic relative z-10">
                  <span className="text-4xl text-[#0EA5E9] absolute -top-4 -left-2 opacity-50">"</span>
                  <span className="pl-6 block">
                    At Vishwa DigiTech, our mission is to empower businesses with innovative digital marketing and cutting-edge IT solutions that drive sustainable growth and create meaningful connections.
                  </span>
                </p>
              </div>

            </div>

            {/* Right side: Custom Mission Image Display */}
            <div className={`lg:col-span-5 relative transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'} order-1 lg:order-2`}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-[#0EA5E9]/20 to-[#1A4A8B]/30 rounded-full blur-3xl -z-10 animate-pulse" />
              
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative bg-[#050810] border border-white/10 rounded-[2rem] overflow-hidden p-4">
                  <div className="relative rounded-[1.5rem] overflow-hidden">
                    <img src={missionImg} alt="Vishwa DigiTech Mission" className="w-full h-auto object-cover transform group-hover:scale-110 transition-transform duration-1000 ease-in-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1F]/80 via-[#0A0F1F]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className={`transition-all duration-1000 delay-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Core <span className="text-[#0EA5E9]">Values</span></h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">The principles that guide our decisions, shape our culture, and drive our success.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:bg-white/10 hover:border-[#0EA5E9]/30 transition-all duration-500 group hover:-translate-y-3 relative overflow-hidden">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0EA5E9]/0 to-[#0EA5E9]/0 group-hover:from-[#0EA5E9]/10 group-hover:to-transparent transition-all duration-500" />

                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-[#0EA5E9]/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[#0EA5E9] transition-all duration-500 shadow-lg">
                    <value.icon className="w-7 h-7 text-[#0EA5E9] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-[#0EA5E9] transition-colors">{value.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutUs;
